import { useCallback, useState } from "react";

import { useLatestRef } from "./useLatestRef";

type UseDisclosureOptions = {
  onOpen?: () => void;
  onClose?: () => void;
};

export const useDisclosure = (
  options: UseDisclosureOptions = {},
): { isOpen: boolean; onOpen: () => void; onClose: () => void; onToggle: () => void } => {
  const [isOpen, setIsOpen] = useState(false);
  const optionsRef = useLatestRef(options);

  const onOpen = useCallback(() => {
    setIsOpen(true);
    optionsRef.current.onOpen?.();
  }, [optionsRef]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    optionsRef.current.onClose?.();
  }, [optionsRef]);

  // Toggles from this render's isOpen, not the pending state, as Chakra does. React events bubble through
  // portals, so a modal rendered inside its own toggle button sees Cancel run onClose and then this onToggle
  // in one event; toggling the pending state would reopen the modal.
  const onToggle = useCallback(() => (isOpen ? onClose() : onOpen()), [isOpen, onOpen, onClose]);

  return { isOpen, onOpen, onClose, onToggle };
};
